<template>
  <div class="financing-table-wrapper">
    <!-- Table Container -->
    <div class="financing-table" :class="{ 'large-dataset': totalRows > 50 }">
      <!-- Grid Header -->
      <div class="grid-header">
        <div class="header-row" :style="{ gridTemplateColumns: gridTemplate }">
          <div 
            v-for="(column, index) in columns" 
            :key="index"
            class="header-cell"
            :class="[column.class, getHeaderAlignmentClass(column)]"
          >
            <i v-if="column.icon" :class="column.icon"></i>
            <span>{{ column.header }}</span>
          </div>
        </div>
      </div>

      <!-- Grid Body -->
      <div class="grid-body">
        <div 
          v-for="(row, rowIndex) in data" 
          :key="getRowKey(row, rowIndex)"
          class="data-row"
          :class="getRowClass(row)"
          :style="{ gridTemplateColumns: gridTemplate }"
          @click="onRowClick(row, rowIndex)"
          v-b-tooltip.hover="getRowTooltip(row)"
        >
          <div 
            v-for="(column, colIndex) in columns" 
            :key="colIndex"
            class="data-cell"
            :class="[column.class, getAlignmentClass(column)]"
            :data-label="column.header"
          >
            <div class="cell-content" :class="getAlignmentClass(column)">
              <!-- Amount columns with currency superscript -->
              <template v-if="column.type === 'currency'">
                <span 
                  class="amount-value" 
                  v-if="getCellValue(row, column) !== null && getCellValue(row, column) !== undefined"
                  v-html="formatCurrency(getCellValue(row, column), getCurrency(row, column))"
                  :title="formatCurrencyTooltip(getCellValue(row, column), getCurrency(row, column))"
                  v-b-tooltip.hover
                ></span>
                <span class="no-data" v-else>-</span>
              </template>
              
              <!-- Status columns with badges -->
              <template v-else-if="column.type === 'status'">
                <span 
                  class="status-badge"
                  :class="getStatusClass(getCellValue(row, column), column.field)"
                  :title="formatStatus(getCellValue(row, column), column.field)"
                  v-b-tooltip.hover
                >
                  {{ formatStatus(getCellValue(row, column), column.field) }}
                </span>
              </template>
              
              <!-- Date columns -->
              <template v-else-if="column.type === 'date'">
                <span 
                  class="date-value" 
                  :class="getDateClass(row, column)"
                  :title="formatDate(getCellValue(row, column))"
                  v-b-tooltip.hover
                >
                  {{ formatDate(getCellValue(row, column)) }}
                </span>
                <span v-if="column.showBadge && getBadgeText(row, column)" class="date-badge">
                  {{ getBadgeText(row, column) }}
                </span>
              </template>
              
              <!-- Currency code columns -->
              <template v-else-if="column.type === 'currency-code'">
                <span 
                  class="currency-code"
                  :title="getCellValue(row, column)"
                  v-b-tooltip.hover
                >
                  {{ getCellValue(row, column) }}
                </span>
              </template>
              
              <!-- Action columns -->
              <template v-else-if="column.type === 'action'">
                <div class="action-buttons">
                  <button 
                    v-if="column.action === 'download' && row[column.field]" 
                    class="invoice-download-btn" 
                    @click.stop="handleAction(column.action, row, column)"
                    :title="column.tooltip || 'Download'"
                  >
                    <i :class="column.icon || 'las la-download'"></i>
                  </button>
                  <button 
                    v-else-if="column.action === 'delete'" 
                    class="delete-btn" 
                    @click.stop="handleAction(column.action, row, column)"
                    :title="column.tooltip || 'Delete'"
                  >
                    <i :class="column.icon || 'las la-trash'"></i>
                  </button>
                  <button
                    v-else-if="column.action === 'silence' && row.status === 'active'"
                    class="silence-btn"
                    @click.stop="handleAction(column.action, row, column)"
                    :title="column.tooltip || 'Silence'"
                  >
                    <i :class="column.icon || 'las la-bell-slash'"></i>
                  </button>
                  <button
                    v-else-if="column.action === 'silence' && row.status === 'silenced'"
                    class="unsilence-btn"
                    @click.stop="handleAction('unsilence', row, column)"
                    title="Unsilence"
                  >
                    <i class="las la-bell"></i>
                  </button>
                  <span v-else-if="column.action === 'silence'" class="no-action">-</span>
                  <span v-else-if="column.action === 'download'" class="no-action">-</span>
                </div>
              </template>
              
              <!-- Default text columns -->
              <template v-else>
                <span 
                  class="text-value"
                  :title="getCellValue(row, column)"
                  v-b-tooltip.hover
                >
                  {{ getCellValue(row, column) }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <base-pagination
        v-if="showPagination"
        :activePage="currentPage"
        :totalRows="totalRows"
        :pageSize="pageSize"
        @set-page="onPageChange"
        :tableName="tableName"
      />
    </div>

    <!-- Empty State -->
    <div v-if="isEmpty" class="empty-state text-center">
      <i :class="emptyIcon" class="la-5x text-muted mb-3"></i>
      <div class="empty-title">{{ emptyTitle }}</div>
      <div v-if="emptySubtitle" class="empty-subtitle">{{ emptySubtitle }}</div>
    </div>
  </div>
</template>

<script>
import BasePagination from "./BasePagination.vue";

export default {
  name: "FinancingTable",
  components: {
    BasePagination
  },
  props: {
    // Table configuration
    columns: {
      type: Array,
      required: true,
      // Each column should have: { header, field, type, align, class, icon, width }
    },
    data: {
      type: Array,
      required: true
    },
    gridTemplate: {
      type: String,
      required: true,
      // CSS grid-template-columns value
    },
    
    // Pagination
    showPagination: {
      type: Boolean,
      default: true
    },
    currentPage: {
      type: Number,
      default: 1
    },
    totalRows: {
      type: Number,
      default: 0
    },
    pageSize: {
      type: Number,
      default: 20
    },
    tableName: {
      type: String,
      default: "financing-table"
    },
    
    // Empty state
    emptyTitle: {
      type: String,
      default: "No data found"
    },
    emptySubtitle: {
      type: String,
      default: ""
    },
    emptyIcon: {
      type: String,
      default: "las la-file"
    },
    
    // Event handlers
    onRowClick: {
      type: Function,
      default: () => {}
    },
    
    // Custom formatters
    formatters: {
      type: Object,
      default: () => ({})
    },
    
    // Status mapping
    statusMap: {
      type: Object,
      default: () => ({})
    },
    
    // Row styling
    rowClass: {
      type: Function,
      default: () => ""
    },
    
    // Tooltip function
    tooltip: {
      type: Function,
      default: () => null
    }
  },
  
  computed: {
    isEmpty() {
      return this.data.length === 0;
    }
  },
  
  methods: {
    getCellValue(row, column) {
      if (column.formatter && this.formatters[column.formatter]) {
        return this.formatters[column.formatter](row[column.field], row);
      }
      return row[column.field];
    },
    
    getCurrency(row, column) {
      if (column.currencyField) {
        return row[column.currencyField];
      }
      return column.defaultCurrency || 'USD';
    },
    
    formatCurrency(amount, currency = 'USD') {
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, currency);
      
      // Format the amount and add currency as superscript
      const formatted = this.$formatCurrency(amount, currency);
      // Remove the currency from the formatted string since we'll add it as superscript
      const amountOnly = formatted.replace(/[A-Z]{3}\s?/, '');
      return `${amountOnly}<sup>${currency}</sup>`;
    },
    
    formatCurrencyTooltip(amount, currency = 'USD') {
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, currency);
      // Return plain text for tooltip (without HTML)
      return this.$formatCurrency(amount, currency);
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatStatus(status, field) {
      // Handle boolean status for is_active field
      if (field === 'is_active') {
        return status ? 'Active' : 'Inactive';
      }
      
      if (this.statusMap[status]) {
        return this.statusMap[status].label || status;
      }
      
      // Default status formatting
      const defaultMap = {
        'DUE': 'Due',
        'OVERDUE': 'Overdue',
        'PAID': 'Paid',
        'PENDING_REVIEW': 'Pending Review',
        'AWAITING_APPROVAL': 'Awaiting approval',
        'APPROVED': 'Approved',
        'REJECTED': 'Rejected',
        'DISBURSED_AMOUNT': 'Disbursed Amount',
        'PARTIALLY_PAID': 'Partially Paid',
        'CLOSED': 'Closed'
      };
      return defaultMap[status] || status;
    },
    
    getStatusClass(status, field) {
      // Handle boolean status for is_active field
      if (field === 'is_active') {
        return status ? 'active' : 'inactive';
      }
      
      if (this.statusMap[status]) {
        return this.statusMap[status].class || 'status-default';
      }
      
      // Default status classes
      const classMap = {
        'DUE': 'status-due',
        'OVERDUE': 'status-overdue',
        'PAID': 'status-paid',
        'PENDING_REVIEW': 'status-pending-review',
        'AWAITING_APPROVAL': 'status-awaiting-approval',
        'APPROVED': 'status-approved',
        'REJECTED': 'status-rejected',
        'DISBURSED_AMOUNT': 'status-disbursed',
        'PARTIALLY_PAID': 'status-partially-paid',
        'CLOSED': 'status-closed'
      };
      return classMap[status] || 'status-default';
    },
    
    getDateClass(row, column) {
      if (column.field === 'due_date' && this.isOverdue(row[column.field])) {
        return 'overdue';
      }
      return '';
    },
    
    getBadgeText(row, column) {
      if (column.field === 'due_date' && this.isOverdue(row[column.field])) {
        return 'Overdue';
      }
      return null;
    },
    
    isOverdue(dueDate) {
      if (!dueDate) return false;
      const due = new Date(dueDate);
      const today = new Date();
      return due < today;
    },
    
    getRowClass(row) {
      let classes = [];
      
      if (this.rowClass) {
        const customClass = this.rowClass(row);
        if (customClass) classes.push(customClass);
      }
      
      return classes.join(' ');
    },
    
    getRowTooltip(row) {
      if (this.tooltip) {
        return this.tooltip(row);
      }
      return null;
    },
    
    getRowKey(row, index) {
      return row.id || row.key || index;
    },
    
    onPageChange(page, pageSize) {
      this.$emit('page-change', page, pageSize);
    },
    
    getAlignmentClass(column) {
      if (column.type === 'currency') {
        return 'text-right';
      }
      if (column.type === 'action') {
        return 'text-center';
      }
      if (column.align) {
        return `text-${column.align}`;
      }
      return 'text-center';
    },
    
    getHeaderAlignmentClass(column) {
      if (column.type === 'action') {
        return 'text-center';
      }
      return '';
    },
    
    handleAction(action, row, column) {
      // Emit the action with row data and column info to parent component
      this.$emit('action', {
        action: action,
        row: row,
        column: column,
        field: column.field
      });
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/sass/variables';

.financing-table-wrapper {
  width: 100%;
  overflow-x: auto;
  
  .financing-table {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 0;
    overflow: hidden;
    min-width: max-content;
    
    &.large-dataset {
      .grid-body {
        max-height: 600px;
        overflow-y: auto;
        overflow-x: visible;
      }
    }
    
    .grid-body {
      overflow-x: visible;
    }
    
    .grid-header {
      background: #f8f9fa;
      border-bottom: 2px solid #dee2e6;
      
      .header-row {
        display: grid;
        gap: 0;
        padding: 0;
        width: 100%;
        
        .header-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.75rem 1rem;
          font-weight: 600;
          color: #495057;
          font-size: 12px;
          font-family: $avenir-medium;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-right: 1px solid #dee2e6;
          text-align: center;
          
          &.text-center {
            justify-content: center !important;
          }
          
          &:last-child {
            border-right: none;
          }
          
          i {
            font-size: 0.75rem;
            color: #6c757d;
            margin-right: 0.25rem;
          }
        }
      }
    }
    
    .grid-body {
      .data-row {
        display: grid;
        gap: 0;
        border-bottom: 1px solid #dee2e6;
        width: 100%;
        transition: all 0.2s ease;
        cursor: pointer;
        overflow: visible;
        
        &:hover {
          background-color: #f8f9fa;
        }
        
        &:last-child {
          border-bottom: none;
        }
        
        &.overdue {
          background-color: #fff5f5;
        }
        
        &.upcoming {
          background-color: #fffbf0;
        }
        
        &.inactive {
          opacity: 0.7;
          background-color: #f8f9fa;
        }
        
        .data-cell {
          display: flex;
          align-items: center;
          padding: 0.875rem 1rem;
          overflow: hidden;
          min-height: 52px;
          min-width: 0;
          
          &.text-center {
            justify-content: center !important;
            text-align: center !important;
            overflow: visible;
          }
          
          &.text-right {
            justify-content: flex-end;
            text-align: right;
            overflow: visible;
          }
          
          &.text-left {
            justify-content: flex-start;
            text-align: left;
          }
          
          // Reduce padding for action columns
          &:has(.action-buttons) {
            padding: 0.875rem 0;
            justify-content: center !important;
            text-align: center !important;
          }
          
          .cell-content {
            font-size: 15px;
            font-family: $avenir-light;
            width: 100%;
            line-height: 1.6;
            
            &.text-center {
              text-align: center;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            
            &.text-right {
              text-align: right;
              display: flex;
              justify-content: flex-end;
              align-items: center;
              overflow: visible;
            }
            
            &.text-left {
              text-align: left;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              overflow: hidden;
              min-width: 0;
            }
            
            .amount-value {
              font-weight: 400;
              color: #212529;
              display: inline-block;
              overflow: visible;
              cursor: help;
              
              sup {
                font-size: 10px;
                font-family: $avenir-medium;
                color: #6c757d;
                margin-left: 2px;
                vertical-align: super;
                line-height: 1;
              }
            }
            
            .text-value {
              color: #212529;
              font-weight: 400;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              display: block;
              width: 100%;
              min-width: 0;
              cursor: help;
            }
            
            .date-value {
              color: #212529;
              font-weight: 400;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              display: block;
              width: 100%;
              min-width: 0;
              cursor: help;
              
              &.overdue {
                color: #dc3545;
                font-weight: 500;
              }
            }
            
            .date-badge {
              background: #dc3545;
              color: white;
              font-size: 10px;
              padding: 0.125rem 0.375rem;
              border-radius: 0.25rem;
              font-weight: 500;
              margin-left: 0.5rem;
              font-family: $avenir-medium;
            }
            
            .status-badge {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-width: 96px;
              padding: 0.375rem 0.75rem;
              font-size: 11px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.025em;
              font-family: $avenir-medium;
              border: 1px solid transparent;
              cursor: help;
              box-sizing: border-box;
              text-align: center;
              white-space: nowrap;
              
              &.status-due {
                background: #fff3cd;
                color: #856404;
                border-color: #ffeaa7;
              }
              
              &.status-paid {
                background: #d4edda;
                color: #155724;
                border-color: #c3e6cb;
              }
              
              &.status-overdue {
                background: #f8d7da;
                color: #721c24;
                border-color: #f5c6cb;
              }
              
              &.status-pending-review {
                background: #e2e3e5;
                color: #383d41;
                border-color: #d6d8db;
              }
              
              &.status-approved {
                background: #d4edda;
                color: #155724;
                border-color: #c3e6cb;
              }
              
              &.status-rejected {
                background: #f8d7da;
                color: #721c24;
                border-color: #f5c6cb;
              }
              
              &.status-disbursed {
                background: #d1ecf1;
                color: #0c5460;
                border-color: #bee5eb;
              }
              
              &.status-partially-paid {
                background: #fff3cd;
                color: #856404;
                border-color: #ffeaa7;
              }
              
              &.status-closed {
                background: #e2e3e5;
                color: #383d41;
                border-color: #d6d8db;
              }

              &.status-awaiting-silence,
              &.status-awaiting-approval {
                min-width: auto;
                padding: 0.35rem 0.6rem;
                font-size: 10px;
                background: #fff7ed;
                color: #9a3412;
                border-color: #fed7aa;
              }
              
              &.status-default {
                background: #e2e3e5;
                color: #383d41;
                border-color: #d6d8db;
              }
              
              &.active {
                background: #d4edda;
                color: #155724;
                border-color: #c3e6cb;
              }
              
              &.inactive {
                background: #f8d7da;
                color: #721c24;
                border-color: #f5c6cb;
              }
            }
            
            .currency-code {
              font-weight: 500;
              color: #6c757d;
              font-family: $avenir-medium;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              display: block;
              width: 100%;
              min-width: 0;
              cursor: help;
            }
            
            .no-data {
              color: #6c757d;
              font-style: italic;
            }
            
            .action-buttons {
              display: flex;
              justify-content: center !important;
              align-items: center;
              gap: 0.5rem;
              width: 100%;
              margin: 0 auto;
              
              .invoice-download-btn {
                background: #2774E9;
                color: white;
                border: none;
                border-radius: 0.25rem;
                padding: 0.25rem 0.35rem;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 0.875rem;
                transition: all 0.2s ease;
                min-width: 28px;
                height: 28px;
                margin: 0 auto;

                &:hover {
                  background: #1a5bb8;
                  transform: translateY(-1px);
                  box-shadow: 0 2px 8px rgba(39, 116, 233, 0.3);
                }

                &:active {
                  transform: translateY(0);
                }
                
                i {
                  font-size: 0.9rem;
                }
              }
              
              .delete-btn {
                background: #e83143;
                color: white;
                border: none;
                border-radius: 0.25rem;
                padding: 0.375rem 0.5rem;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 0.875rem;
                transition: all 0.2s ease;

                &:hover {
                  background: #c82333;
                  transform: translateY(-1px);
                  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
                }

                &:active {
                  transform: translateY(0);
                }
                
                i {
                  font-size: 1rem;
                }
              }

              .silence-btn,
              .unsilence-btn {
                background: transparent;
                border: none;
                border-radius: 0;
                padding: 0.15rem;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-shadow: none;
                transform: none;

                i {
                  font-size: 1.5rem;
                  line-height: 1;
                  color: #9ca3af;
                  transition: color 0.15s ease;
                }

                &:hover,
                &:active {
                  background: transparent;
                  box-shadow: none;
                  transform: none;
                }
              }

              .silence-btn:hover i {
                color: #6c757d;
              }

              .unsilence-btn:hover i {
                color: #2874E9;
              }
              
              .no-action {
                color: #6c757d;
                font-style: italic;
              }
            }
          }
        }
      }
    }
  }
  
  .empty-state {
    padding: 3rem 1rem;
    
    .empty-title {
      font-size: 1.25rem;
      font-weight: 500;
      color: #6b7280;
      margin-bottom: 0.5rem;
      font-family: $avenir-medium;
    }
    
    .empty-subtitle {
      color: #9ca3af;
      font-family: $avenir-light;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .financing-table-wrapper {
    .financing-table {
      .grid-header {
        display: none;
      }
      
      .grid-body {
        .data-row {
          display: block;
          border: 1px solid #dee2e6;
          border-radius: 0.25rem;
          margin-bottom: 1rem;
          padding: 1rem;
          
          .data-cell {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0;
            border-right: none;
            border-bottom: 1px solid #f8f9fa;
            
            &:last-child {
              border-bottom: none;
            }
            
            &::before {
              content: attr(data-label) ": ";
              font-weight: 600;
              color: #495057;
              font-family: $avenir-medium;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .cell-content {
              text-align: right;
              flex: 1;
            }
          }
        }
      }
    }
  }
}
</style>
